import classNames from "classnames";
import { FC, memo, MouseEvent, useMemo, useRef, useState } from "react";

import styles from "./style.module.scss";

export type ChartSeries = {
  color: string;
  deviceId: string;
  location: string;
  values: (number | null)[];
};

export type ChartRefLine =
  | { kind: "h"; y: number; color: "red" | "cyan" }
  | { kind: "wave"; values: (number | null)[]; color: "red" | "cyan" };

type PropTypes = {
  series: ChartSeries[];
  yMin: number;
  yMax: number;
  yTicks: number[];
  peakLabel: string;
  pointTimes: string[];
  xLabels: { text: string; at: number }[];
  valueUnit: string;
  format: (value: number) => string;
  refLines?: ChartRefLine[];
  threshold?: number | null;
  thresholdNote?: string;
};

const VIEW_WIDTH = 320;
const HEIGHT = 200;
const PLOT = { left: 6, right: 314, top: 10, bottom: 168 };

const HistoricalChart: FC<PropTypes> = ({
  series,
  yMin,
  yMax,
  yTicks,
  peakLabel,
  pointTimes,
  xLabels,
  valueUnit,
  format,
  refLines = [],
  threshold = null,
  thresholdNote,
}) => {
  const [hovered, setHovered] = useState<number | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  const count = pointTimes.length;

  const pointX = useMemo(
    () => (index: number) =>
      count <= 1 ? PLOT.left : PLOT.left + (index / (count - 1)) * (PLOT.right - PLOT.left),
    [count],
  );
  const pointY = useMemo(
    () => (value: number) => {
      const span = yMax - yMin;
      const frac = span > 0 ? Math.min(1, Math.max(0, (value - yMin) / span)) : 0;
      return PLOT.bottom - frac * (PLOT.bottom - PLOT.top);
    },
    [yMin, yMax],
  );

  const seriesSegments = useMemo(
    () =>
      series.map((item) => {
        const segments: { x: number; y: number }[][] = [];
        let current: { x: number; y: number }[] = [];
        item.values.forEach((value, index) => {
          if (value == null) {
            if (current.length) segments.push(current);
            current = [];
          } else {
            current.push({ x: pointX(index), y: pointY(value) });
          }
        });
        if (current.length) segments.push(current);
        return segments;
      }),
    [series, pointX, pointY],
  );

  const handleMove = (event: MouseEvent<HTMLDivElement>) => {
    const element = chartRef.current;
    if (!element || count === 0) return;
    const rect = element.getBoundingClientRect();
    const ratio = (event.clientX - rect.left) / rect.width;
    setHovered(Math.min(count - 1, Math.max(0, Math.round(ratio * (count - 1)))));
  };

  const guideX = hovered === null ? 0 : pointX(hovered);

  const toneColor = (value: number | null): string | null => {
    if (value == null || threshold == null) return null;
    if (value <= threshold * 0.9) return "var(--green)";
    if (value <= threshold) return "var(--orange)";
    return "var(--red)";
  };

  return (
    <div className={styles.plot}>
      <div className={styles.yAxis}>
        {yTicks.map((tick) => (
          <span key={tick} className={styles.yLabel}>
            {format(tick)}
          </span>
        ))}
      </div>

      <div className={styles.chart} ref={chartRef} onMouseMove={handleMove} onMouseLeave={() => setHovered(null)}>
        <svg className={styles.svg} viewBox={`0 0 ${VIEW_WIDTH} ${HEIGHT}`} preserveAspectRatio="none" role="img">
          {refLines.map((line, index) =>
            line.kind === "h" ? (
              <line
                key={`ref-${index}`}
                x1={PLOT.left}
                x2={PLOT.right}
                y1={pointY(line.y)}
                y2={pointY(line.y)}
                className={classNames(styles.refLine, styles[`ref-${line.color}`])}
                vectorEffect="non-scaling-stroke"
              />
            ) : (
              <path
                key={`ref-${index}`}
                d={line.values
                  .map((value, i) => (value == null ? "" : `${i === 0 ? "M" : "L"} ${pointX(i)} ${pointY(value)}`))
                  .filter(Boolean)
                  .join(" ")}
                fill="none"
                className={classNames(styles.refLine, styles[`ref-${line.color}`])}
                vectorEffect="non-scaling-stroke"
              />
            ),
          )}

          {seriesSegments.map((segments, sIndex) =>
            segments.map((segment, segIndex) => (
              <polyline
                key={`series-${sIndex}-${segIndex}`}
                points={segment.map((p) => `${p.x},${p.y}`).join(" ")}
                fill="none"
                stroke={series[sIndex].color}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            )),
          )}

          {series.map((item, sIndex) =>
            item.values.map((value, index) =>
              value == null ? null : (
                <circle
                  key={`pt-${sIndex}-${index}`}
                  cx={pointX(index)}
                  cy={pointY(value)}
                  r={2.5}
                  fill={item.color}
                  vectorEffect="non-scaling-stroke"
                />
              ),
            ),
          )}

          {hovered !== null && (
            <line
              x1={guideX}
              x2={guideX}
              y1={PLOT.top}
              y2={PLOT.bottom}
              className={styles.guide}
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>

        {hovered !== null && (
          <div
            className={styles.tooltip}
            style={{ left: `${(guideX / VIEW_WIDTH) * 100}%` }}
          >
            <span className={styles.tooltipTime}>{`${pointTimes[hovered]} · ${peakLabel}`}</span>

            {series.map((item, index) => {
              const value = item.values[hovered];
              const tone = toneColor(value);
              return (
                <span key={`tip-${index}`} className={styles.tooltipRow}>
                  <span className={styles.tooltipDot} style={{ background: item.color }} />

                  <span className={styles.tooltipText}>
                    <span className={styles.tooltipDevice}>Device: {item.deviceId}</span>
                    <span className={styles.tooltipLoc}>Location: {item.location}</span>
                    <span className={styles.tooltipValue} style={tone ? { color: tone } : undefined}>
                      {value == null ? "No data" : `${format(value)} ${valueUnit}`}
                    </span>
                  </span>
                </span>
              );
            })}

            {thresholdNote && <span className={styles.tooltipNote}>{thresholdNote}</span>}
          </div>
        )}

        <div className={styles.xAxis}>
          {xLabels.map((label) => (
            <span
              key={`${label.text}-${label.at}`}
              className={styles.xLabel}
              style={{ left: `${(PLOT.left + label.at * (PLOT.right - PLOT.left)) / VIEW_WIDTH * 100}%` }}
            >
              {label.text}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default memo(HistoricalChart);
